<template>
  <div class="api-allocation-page">
    <div class="header-row">
      <div>
        <h2>API订单分配管理</h2>
        <p>处理上游API推送后尚未完成管理员、业务员归属的订单</p>
      </div>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model.trim="filters.order_no" clearable placeholder="订单号精确查询" @keyup.enter.native="search" />
        <el-input v-model.trim="filters.keyword" clearable placeholder="收件人/电话/上游单号" @keyup.enter.native="search" />
        <el-select v-model="filters.business_type" clearable filterable placeholder="业务类型">
          <el-option v-for="item in businessTypeOptions" :key="item.id" :label="item.name" :value="item.name" />
        </el-select>
        <el-select v-model="filters.api_allocation_management" clearable placeholder="分配状态">
          <el-option label="未分配完成" :value="0" />
          <el-option label="已分配管理员" :value="2" />
        </el-select>
        <el-input v-model.trim="filters.new_phone" clearable placeholder="新开号码" @keyup.enter.native="search" />
        <el-input v-model.trim="filters.team_name" clearable placeholder="归属团队" @keyup.enter.native="search" />
        <el-input v-model.trim="filters.address_keyword" clearable placeholder="收件地址" @keyup.enter.native="search" />
        <el-select v-model="filters.source" clearable filterable placeholder="建单来源">
          <el-option v-for="item in orderSourceOptions" :key="item.id" :label="item.name" :value="item.name" />
        </el-select>
        <el-select v-model="filters.is_urgent" clearable placeholder="是否加急">
          <el-option label="加急" :value="true" />
          <el-option label="不加急" :value="false" />
        </el-select>
        <el-button type="primary" @click="search">查询</el-button>
        <el-button plain @click="resetFilters">重置</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table v-loading="loading" :data="orders" border stripe>
        <el-table-column prop="order_no" label="订单号" min-width="170" />
        <el-table-column prop="upstream_order_no" label="上游单号" min-width="150" />
        <el-table-column prop="recipient_name" label="收件人" min-width="100" />
        <el-table-column prop="recipient_phone" label="联系电话" min-width="130" />
        <el-table-column prop="business_type" label="业务类型" min-width="120" />
        <el-table-column label="分配状态" min-width="120">
          <template slot-scope="{ row }">{{ allocationStatusText(row.api_allocation_management) }}</template>
        </el-table-column>
        <el-table-column prop="new_phone" label="新开号码" min-width="130" />
        <el-table-column prop="team_name" label="归属团队" min-width="160" />
        <el-table-column label="收件地址" min-width="240">
          <template slot-scope="{ row }">{{ row.full_address || row.detail_address || "-" }}</template>
        </el-table-column>
        <el-table-column label="建单来源" min-width="140">
          <template slot-scope="{ row }">{{ row.channel || row.source || "-" }}</template>
        </el-table-column>
        <el-table-column label="是否加急" min-width="90">
          <template slot-scope="{ row }">{{ row.is_urgent ? "是" : "否" }}</template>
        </el-table-column>
        <el-table-column prop="manager_name" label="管理员" min-width="110" />
        <el-table-column prop="sales_name" label="业务员" min-width="110" />
        <el-table-column label="操作" fixed="right" width="240">
          <template slot-scope="{ row }">
            <div class="action-cell">
              <el-button size="mini" plain @click="openDetail(row)">详情</el-button>
              <el-button v-if="canGrab" size="mini" type="primary" plain @click="grab(row)">抢单</el-button>
              <el-button v-if="canAssign" size="mini" type="success" plain @click="openAssign(row)">分配</el-button>
              <el-button v-if="canReturn" size="mini" type="warning" plain @click="returnOrder(row)">退单</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager-row">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="[10, 20, 50, 100]"
          :current-page.sync="pager.page"
          :page-size.sync="pager.page_size"
          :total="total"
          @current-change="loadOrders"
          @size-change="handleSizeChange"
        />
      </div>
    </div>

    <el-dialog title="API订单详情" :visible.sync="detailVisible" width="720px">
      <el-descriptions v-if="detail" :column="2" border>
        <el-descriptions-item label="订单号">{{ detail.order_no }}</el-descriptions-item>
        <el-descriptions-item label="上游单号">{{ detail.upstream_order_no || "-" }}</el-descriptions-item>
        <el-descriptions-item label="收件人">{{ detail.recipient_name }}</el-descriptions-item>
        <el-descriptions-item label="联系电话">{{ detail.recipient_phone }}</el-descriptions-item>
        <el-descriptions-item label="业务类型">{{ detail.business_type || "-" }}</el-descriptions-item>
        <el-descriptions-item label="订单来源">{{ detail.channel || "-" }}</el-descriptions-item>
        <el-descriptions-item label="管理员">{{ detail.manager_name || "-" }}</el-descriptions-item>
        <el-descriptions-item label="业务员">{{ detail.sales_name || "-" }}</el-descriptions-item>
        <el-descriptions-item label="地址" :span="2">{{ detail.full_address || detail.detail_address || "-" }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ detail.remark || "-" }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <el-dialog title="分配API订单" :visible.sync="assignVisible" width="520px">
      <el-form label-width="90px">
        <el-form-item v-if="isSuperAdmin" label="管理员">
          <el-select v-model="assignForm.manager_id" filterable clearable placeholder="请选择管理员" @change="loadSalesOptions">
            <el-option v-for="item in managerOptions" :key="item.id" :label="userLabel(item)" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="业务员">
          <el-select v-model="assignForm.sales_id" filterable clearable placeholder="请选择业务员">
            <el-option v-for="item in salesOptions" :key="item.id" :label="userLabel(item)" :value="item.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="assignVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAssign">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  assignApiAllocationOrder,
  fetchApiAllocationOrderDetail,
  fetchApiAllocationOrders,
  fetchBusinessTypes,
  fetchOrderSources,
  fetchUsers,
  grabApiAllocationOrder,
  returnApiAllocationOrder,
} from "../api/orders";
import { getStoredUser } from "../utils/auth";

export default {
  data() {
    return {
      loading: false,
      orders: [],
      total: 0,
      filters: this.defaultFilters(),
      pager: { page: 1, page_size: 10 },
      businessTypeOptions: [],
      orderSourceOptions: [],
      detailVisible: false,
      detail: null,
      assignVisible: false,
      assigningOrder: null,
      assignForm: { manager_id: null, sales_id: null },
      managerOptions: [],
      salesOptions: [],
    };
  },
  computed: {
    currentUser() {
      return getStoredUser();
    },
    isSuperAdmin() {
      return this.currentUser.role === "super_admin";
    },
    canGrab() {
      return ["admin", "sales"].includes(this.currentUser.role);
    },
    canAssign() {
      return ["super_admin", "admin"].includes(this.currentUser.role);
    },
    canReturn() {
      return ["admin", "sales"].includes(this.currentUser.role);
    },
  },
  mounted() {
    this.loadOrders();
    this.loadOptions();
  },
  methods: {
    defaultFilters() {
      return {
        order_no: "",
        keyword: "",
        business_type: "",
        api_allocation_management: "",
        new_phone: "",
        team_name: "",
        address_keyword: "",
        source: "",
        is_urgent: "",
      };
    },
    buildParams() {
      return {
        ...this.filters,
        api_allocation_management:
          this.filters.api_allocation_management === "" ? undefined : this.filters.api_allocation_management,
        is_urgent: this.filters.is_urgent === "" ? undefined : this.filters.is_urgent,
        page: this.pager.page,
        page_size: this.pager.page_size,
      };
    },
    async loadOrders() {
      this.loading = true;
      try {
        const { data } = await fetchApiAllocationOrders(this.buildParams());
        this.orders = data.items || [];
        this.total = data.total || 0;
      } finally {
        this.loading = false;
      }
    },
    async loadOptions() {
      const [businessTypes, orderSources] = await Promise.all([
        fetchBusinessTypes({ page: 1, page_size: 1000, is_active: true }),
        fetchOrderSources({ page: 1, page_size: 1000, is_active: true }),
      ]);
      this.businessTypeOptions = businessTypes.data.items || [];
      this.orderSourceOptions = orderSources.data.items || [];
    },
    search() {
      this.pager.page = 1;
      this.loadOrders();
    },
    resetFilters() {
      this.filters = this.defaultFilters();
      this.search();
    },
    handleSizeChange() {
      this.pager.page = 1;
      this.loadOrders();
    },
    allocationStatusText(value) {
      if (value === 2) return "已分配管理员";
      if (value === 1) return "分配完成";
      return "未分配完成";
    },
    userLabel(item) {
      return item.phone ? `${item.name}（${item.phone}）` : item.name;
    },
    async openDetail(row) {
      const { data } = await fetchApiAllocationOrderDetail(row.id);
      this.detail = data;
      this.detailVisible = true;
    },
    async grab(row) {
      await this.$confirm(`确认领取订单 ${row.order_no}？`, "提示", { type: "warning" });
      await grabApiAllocationOrder(row.id);
      this.$message.success("抢单成功");
      this.loadOrders();
    },
    async returnOrder(row) {
      await this.$confirm(`确认退回订单 ${row.order_no}？`, "提示", { type: "warning" });
      await returnApiAllocationOrder(row.id);
      this.$message.success("退单成功");
      this.loadOrders();
    },
    async openAssign(row) {
      this.assigningOrder = row;
      this.assignForm = { manager_id: row.manager_id || null, sales_id: row.sales_id || null };
      if (this.isSuperAdmin) {
        await this.loadManagers();
      } else {
        this.assignForm.manager_id = this.currentUser.id;
      }
      await this.loadSalesOptions();
      this.assignVisible = true;
    },
    async loadManagers() {
      const { data } = await fetchUsers({ role: "admin", page: 1, page_size: 1000, is_active: true });
      this.managerOptions = data.items || [];
    },
    async loadSalesOptions() {
      const params = { role: "sales", page: 1, page_size: 1000, is_active: true };
      if (this.assignForm.manager_id) {
        params.leader_id = this.assignForm.manager_id;
      }
      const { data } = await fetchUsers(params);
      this.salesOptions = data.items || [];
      if (this.assignForm.sales_id && !this.salesOptions.some((item) => item.id === this.assignForm.sales_id)) {
        this.assignForm.sales_id = null;
      }
    },
    async submitAssign() {
      if (!this.assigningOrder) return;
      if (this.isSuperAdmin && !this.assignForm.manager_id) {
        this.$message.warning("请选择管理员");
        return;
      }
      if (!this.assignForm.sales_id) {
        this.$message.warning("请选择业务员");
        return;
      }
      await assignApiAllocationOrder(this.assigningOrder.id, {
        manager_id: this.isSuperAdmin ? this.assignForm.manager_id : undefined,
        sales_id: this.assignForm.sales_id,
      });
      this.$message.success("分配成功");
      this.assignVisible = false;
      this.loadOrders();
    },
  },
};
</script>

<style scoped>
.api-allocation-page {
  padding: 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.header-row h2 {
  margin: 0 0 8px;
}

.header-row p {
  margin: 0;
  color: #60718f;
}

.filter-card,
.table-card {
  padding: 18px;
}

.filter-card {
  margin-bottom: 18px;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: -6px;
}

.toolbar-row > * {
  margin: 6px;
}

.toolbar-row :deep(.el-input),
.toolbar-row :deep(.el-select) {
  width: 220px;
}

.pager-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-cell :deep(.el-button) {
  margin-left: 0;
}
</style>
